import { CoreBaseService } from "@core/services/Base.service";

export interface IReportExportParams {
  client_ids?: number[];
  date_start: string;
  date_end: string;
}

export interface IReportExportCreated {
  id: number;
  status: string;
}

export interface IReportExportStatus {
  id: number;
  status: "pending" | "processing" | "ready" | "failed";
  total: number | null;
  uploaded: number | null;
  missing: number | null;
  error: string | null;
  ready: boolean;
  download_url: string | null;
}

const isSuccess = (status: any): boolean =>
  typeof status === "number" && status >= 200 && status < 300;

class ReportsService extends CoreBaseService {
  constructor() {
    super(null, "reports");
  }

  /**
   * Enqueue an asynchronous report export job.
   * When client_ids is empty/omitted the backend exports reports for all clients.
   * date_start/date_end are required and must be formatted as YYYY-MM-DD.
   * Returns the 202 body: { id, status: "pending" }.
   */
  createExport(params: IReportExportParams): Promise<IReportExportCreated> {
    const body: Record<string, any> = {
      date_start: params.date_start,
      date_end: params.date_end,
    };

    if (params.client_ids && params.client_ids.length) {
      body.client_ids = params.client_ids;
    }

    // 202 Accepted is a success here; hasErrors() only treats 200 as OK, so
    // check the status range explicitly instead.
    return this.sub("export")
      .create(body, true)
      .then((res: any) => {
        if (!res || !isSuccess(res.status)) {
          throw res;
        }
        const data = typeof res.getData === "function" ? res.getData() : res;
        return data as IReportExportCreated;
      });
  }

  /**
   * Poll the status of an export job.
   * download_url is set only when the job is ready.
   */
  getExportStatus(id: number): Promise<IReportExportStatus> {
    return this.sub("export")
      .get(id, undefined, true)
      .then((res: any) => {
        if (!res || !isSuccess(res.status)) {
          throw res;
        }
        const data = typeof res.getData === "function" ? res.getData() : res;
        return data as IReportExportStatus;
      });
  }

  /**
   * Download the ready ZIP and trigger a browser save.
   * Uses a blob response so we can stream the ZIP; only saves on a real success.
   */
  downloadExport(id: number, filename: string): Promise<void> {
    return this.sub("export")
      .sub(`${id}/download`)
      .get(undefined, undefined, true, false, { responseType: "blob" })
      .then((res: any) => {
        if (!res || !isSuccess(res.status)) {
          throw res;
        }
        if (typeof res.download === "function") {
          res.download(filename);
        }
      });
  }
}

const inst = new ReportsService();

export default inst;
